import { debounce } from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { IColumnSetting } from 'src/common/ESDataTable';
import IDataTableManager from 'src/common/IDataTableManager';
import ITableOptions from 'src/common/ITableOptions';
import { ColumnSettingsDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';

class DataTableStore<TDto, KEntity> implements IDataTableManager<KEntity> {
    @observable
    public columns: IColumnSetting[] = [];
    @observable
    public count: number = 0;
    @observable
    public data: KEntity[] = [];
    @observable
    public isLoading: boolean = false;
    @observable
    public tableOptions: ITableOptions = {
        filter: '',
        includeInactives: false,
        page: 1,
        showCompleted: false,
    };

    private debouncedSaveColumnSettings = debounce(this.saveColumnSettings, 600);
    private debouncedLoadData = debounce(this.loadData, 600);

    constructor(
        protected globalStore: GlobalStore,
        private getData: (options: ITableOptions) => Promise<SwaggerResponse<TDto[]>>,
        private mapData: (data: TDto[]) => KEntity[],
        private saveColumnSettings: (userId: string, columns: IColumnSetting[]) => void
    ) {}

    @action.bound
    public setColumns(columns: ColumnSettingsDto[]) {
        this.columns = columns;
    }

    @action.bound
    public setTableOrderBy(orderBy?: string) {
        this.tableOptions.orderBy = orderBy;
    }

    @action.bound
    public updateColumnSettings(columns: IColumnSetting[]) {
        this.setColumns(columns);
        this.debouncedSaveColumnSettings(this.globalStore.user!.id, columns);
    }

    @action.bound
    public updateTableOptions(tableOptions: ITableOptions) {
        this.tableOptions = tableOptions;
        this.debouncedLoadData();
    }

    @action.bound
    public loadData() {
        this.isLoading = true;
        return this.getData(this.tableOptions)
            .then((resp: SwaggerResponse<TDto[]>) => {
                runInAction(() => {
                    this.count = this.getTotalCountFromHeader(resp);
                    this.data = this.mapData(resp.result);
                });
            })
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action.bound
    public clearFilter() {
        this.tableOptions.filter = '';
    }

    private getTotalCountFromHeader(resp: SwaggerResponse<TDto[]>): number {
        const header = JSON.parse(resp.headers['x-pagination'] || '{"TotalCount": 0}');
        return header.TotalCount;
    }
}

export default DataTableStore;



// WEBPACK FOOTER //
// ./src/stores/DataTableStore/DataTableStore.ts